import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FeePlanInput } from '@academy/types'
import { api } from '../lib/api.js'
import { friendlyError } from '../lib/errors.js'
import { Card, CardBody, CardHeader } from './Card.js'
import { Button } from './Button.js'
import { Money } from './Money.js'
import { StatusPill } from './StatusPill.js'
import { FeePlanSection } from './FeePlanSection.js'

interface PlanInstallment {
  id: string
  seq: number
  amountInr: number
  generateOn: string
  generated: boolean
  invoiceNo: string | null
  invoiceStatus: string | null
  invoicePaidInr: number | null
  invoiceBalanceInr: number | null
}

interface FeePlan {
  id: string
  totalAmountInr: number
  discountInr: number
  startDate: string | null
  endDate: string | null
  isActive: boolean
  notes: string | null
  installments: PlanInstallment[]
}

const fmtDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—'

/** Fee-plan panel on the student detail page: shows the schedule, or a "set up
 * plan" editor when none exists, and lets the admin deactivate an active plan. */
export function FeePlanPanel({ studentId }: { studentId: string }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<FeePlanInput | null>(null)

  const planQuery = useQuery({
    queryKey: ['fee-plan', studentId],
    queryFn: async () => {
      const res = await api<FeePlan | null>(`/api/v1/fee-plans?studentId=${studentId}`)
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const savePlan = useMutation({
    mutationFn: async () => {
      // Editing an existing plan → PATCH (keeps already-generated invoices,
      // replaces the future schedule). Otherwise POST a new plan.
      const existingId = planQuery.data?.id
      const res = existingId
        ? await api(`/api/v1/fee-plans/${existingId}`, {
            method: 'PATCH',
            body: JSON.stringify(draft),
          })
        : await api(`/api/v1/fee-plans`, {
            method: 'POST',
            body: JSON.stringify({ studentId, ...draft }),
          })
      if ('error' in res) throw new Error(friendlyError(res.error))
      return res.data
    },
    onSuccess: () => {
      setEditing(false)
      setDraft(null)
      qc.invalidateQueries({ queryKey: ['fee-plan', studentId] })
    },
    onError: (err) => alert(err instanceof Error ? err.message : 'Could not save plan'),
  })

  const deactivate = useMutation({
    mutationFn: async (planId: string) => {
      const res = await api(`/api/v1/fee-plans/${planId}`, { method: 'DELETE' })
      if ('error' in res) throw new Error(friendlyError(res.error))
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fee-plan', studentId] }),
    onError: (err) => alert(err instanceof Error ? err.message : 'Could not deactivate'),
  })

  const plan = planQuery.data

  if (editing) {
    // When editing an existing plan, pre-fill the form with only the not-yet-
    // invoiced installments — those are all the PATCH can change. Already-invoiced
    // installments are live invoices (possibly with payments), so they're shown
    // read-only above the form rather than as editable rows.
    const existing = planQuery.data
    const invoiced = existing?.installments.filter((i) => i.generated) ?? []
    const initial = existing
      ? {
          totalRupees: String(existing.totalAmountInr / 100),
          discountRupees: existing.discountInr ? String(existing.discountInr / 100) : '',
          startDate: existing.startDate ?? undefined,
          endDate: existing.endDate ?? undefined,
          installments: existing.installments
            .filter((i) => !i.generated)
            .map((i) => ({
              amountRupees: String(i.amountInr / 100),
              generateOn: i.generateOn,
            })),
        }
      : undefined

    return (
      <div className="space-y-4">
        {invoiced.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-fg-subtle">
              Already invoiced (kept as-is)
            </p>
            <ul className="divide-y divide-border rounded-md border border-border">
              {invoiced.map((inst) => (
                <li
                  key={inst.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-6 text-xs text-fg-subtle">#{inst.seq}</span>
                    <Money paise={inst.amountInr} variant="data" />
                    <span className="font-mono text-xs text-fg-subtle">{inst.invoiceNo}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    {inst.invoiceStatus === 'partial' && inst.invoiceBalanceInr != null ? (
                      <span className="text-xs text-warning">
                        <Money paise={inst.invoiceBalanceInr} variant="data" /> due
                      </span>
                    ) : null}
                    {inst.invoiceStatus ? (
                      <StatusPill
                        tone={
                          inst.invoiceStatus as
                            | 'pending'
                            | 'partial'
                            | 'paid'
                            | 'overdue'
                            | 'waived'
                        }
                      >
                        {inst.invoiceStatus}
                      </StatusPill>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-fg-subtle">
              These have live invoices and can't be changed here. Edits apply to the upcoming
              schedule below.
            </p>
          </div>
        ) : null}

        <div className="space-y-2">
          {invoiced.length > 0 ? (
            <p className="text-xs font-medium uppercase tracking-wide text-fg-subtle">
              Upcoming schedule
            </p>
          ) : null}
          <FeePlanSection alwaysOn initial={initial} onChange={setDraft} />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => { setEditing(false); setDraft(null) }}>
            Cancel
          </Button>
          <Button onClick={() => savePlan.mutate()} disabled={!draft || savePlan.isPending}>
            {savePlan.isPending ? 'Saving…' : 'Save fee plan'}
          </Button>
        </div>
      </div>
    )
  }

  if (!plan) {
    return (
      <Card>
        <CardHeader title="Fee plan" description="Schedule invoices that auto-generate and notify the parent." />
        <CardBody>
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Set up fee plan
          </Button>
        </CardBody>
      </Card>
    )
  }

  const netPaise = plan.totalAmountInr - plan.discountInr

  return (
    <Card>
      <CardHeader
        title="Fee plan"
        description={`${fmtDate(plan.startDate)} → ${fmtDate(plan.endDate)}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm('Deactivate this fee plan? Future installments stop generating; existing invoices are kept.'))
                  deactivate.mutate(plan.id)
              }}
            >
              Deactivate
            </Button>
          </div>
        }
      />
      <CardBody className="space-y-3">
        <div className="flex gap-6 text-sm">
          <span className="text-fg-muted">Total <Money paise={plan.totalAmountInr} variant="data" /></span>
          {plan.discountInr > 0 ? (
            <span className="text-fg-muted">Discount <Money paise={plan.discountInr} variant="data" /></span>
          ) : null}
          <span className="text-fg font-medium">Net <Money paise={netPaise} variant="data" /></span>
        </div>
        <ul className="divide-y divide-border">
          {plan.installments.map((inst) => (
            <li key={inst.id} className="flex items-center justify-between py-2 text-sm">
              <span className="flex items-center gap-3">
                <span className="w-6 text-xs text-fg-subtle">#{inst.seq}</span>
                <span className="text-fg-muted">
                  {new Date(inst.generateOn).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
                <Money paise={inst.amountInr} variant="data" />
              </span>
              <span className="flex items-center gap-2">
                {inst.generated ? (
                  <>
                    {/* Show the outstanding balance once a partial payment lands. */}
                    {inst.invoiceStatus === 'partial' && inst.invoiceBalanceInr != null ? (
                      <span className="text-xs text-warning">
                        <Money paise={inst.invoiceBalanceInr} variant="data" /> due
                      </span>
                    ) : null}
                    <span className="font-mono text-xs text-fg-subtle">{inst.invoiceNo}</span>
                    {inst.invoiceStatus ? (
                      <StatusPill
                        tone={
                          inst.invoiceStatus as
                            | 'pending'
                            | 'partial'
                            | 'paid'
                            | 'overdue'
                            | 'waived'
                        }
                      >
                        {inst.invoiceStatus}
                      </StatusPill>
                    ) : null}
                  </>
                ) : (
                  <span className="text-xs text-fg-subtle">Scheduled</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}
